import { useId, useState, type FormEvent } from 'react'
import { useMe } from '@/auth/useMe'
import { RichTextEditor } from '@/components/RichTextEditor'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, useDirtyGuard } from '@/components/ui/dialog'
import { FormField } from '@/components/ui/form-field'
import { Input } from '@/components/ui/input'
import { NativeSelect } from '@/components/ui/native-select'
import { useTeamMembers } from '@/features/preinvest/useTeamMembers'
import { toastFailure } from '@/lib/feedback'
import { toast } from '@/lib/toast'
import { CategorySelect } from '../components/CategorySelect'
import { todayIso } from '../dates'
import { AdvancedFilterSheet } from '../filters/AdvancedFilterSheet'
import { useFilterOptions } from '../filters/useFilterOptions'
import { CrmApiError } from '../http'
import { countRecords, getRegister } from '../query/api'
import { normalizeFilter } from '../query/codec'
import type { Filter, Register } from '../query/types'
import { clearDraft, readDraft, writeDraft } from '../timeline/drafts'
import type { CrmCategoryRef, ListType } from '../types'
import { createListV2, updateListV2 } from './api'
import type { ListDetail, ListInput } from './types'

const EMPTY_FILTER: Filter = normalizeFilter({})
const contactNoun = (count: number): string => (count === 1 ? '1 contact' : `${count} contacten`)

interface Draft {
  name: string
  kind: CrmCategoryRef | null
  starts_on: string
  ends_on: string
  location: string
  owner: string
  description_html: string
  type: ListType
  filter: Filter
}

const isDraft = (value: unknown): value is Draft =>
  typeof value === 'object' &&
  value !== null &&
  typeof (value as Draft).name === 'string' &&
  typeof (value as Draft).filter === 'object' &&
  ((value as Draft).type === 'static' || (value as Draft).type === 'dynamic')

const draftKey = (list: ListDetail | undefined): string => `crm:lijst:${list ? list.id : 'nieuw'}`

function fromList(list: ListDetail): Draft {
  return {
    name: list.name,
    kind: list.kind,
    starts_on: list.starts_on ?? '',
    ends_on: list.ends_on ?? '',
    location: list.location ?? '',
    owner: list.owner?.id ?? '',
    description_html: list.description_html ?? '',
    type: list.type,
    filter: list.filter ? normalizeFilter(list.filter) : EMPTY_FILTER,
  }
}

const emptyDraft = (owner: string): Draft => ({
  name: '',
  kind: null,
  starts_on: '',
  ends_on: '',
  location: '',
  owner,
  description_html: '',
  type: 'static',
  filter: EMPTY_FILTER,
})

/** What the server sends, as the draft is: `filter` only goes along for a dynamic list. */
function toInput(draft: Draft): ListInput {
  return {
    name: draft.name.trim(),
    type: draft.type,
    kind_id: draft.kind?.id ?? null,
    starts_on: draft.starts_on || null,
    ends_on: draft.ends_on || null,
    location: draft.location.trim() || null,
    description_html: draft.description_html || null,
    owner_user_id: draft.owner || null,
    ...(draft.type === 'dynamic' ? { filter: draft.filter } : {}),
  }
}

const conditionCount = (filter: Filter): number => filter.quick.length + filter.groups.reduce((sum, group) => sum + group.length, 0)

/**
 * New or edited list (spec-63 E5, design §4.7; `Dialog size="lg"`): name, kind, date or period, place,
 * owner, rich description and the type with, for a dynamic list, the filter on Contacten. The type is
 * chosen when the list is made; an existing list changes type through "Omzetten naar statisch". The
 * draft lives in `sessionStorage` until the list is saved or the dialog is closed on purpose (ED-52).
 */
export function ListDialog({
  open,
  onOpenChange,
  list,
  onSaved,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Edit this list instead of making a new one. */
  list?: ListDetail
  onSaved: (list: ListDetail) => void
}) {
  const key = draftKey(list)
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // Reaching here means the dialog closes for good (a dirty one asks first): the draft goes with it.
        if (!next) clearDraft(key)
        onOpenChange(next)
      }}
    >
      {open ? <ListForm list={list} draftKey={key} onOpenChange={onOpenChange} onSaved={onSaved} /> : null}
    </Dialog>
  )
}

function ListForm({
  list,
  draftKey: key,
  onOpenChange,
  onSaved,
}: {
  list: ListDetail | undefined
  draftKey: string
  onOpenChange: (open: boolean) => void
  onSaved: (list: ListDetail) => void
}) {
  const group = useId()
  const { me } = useMe()
  const { membersById } = useTeamMembers()
  const [draft, setDraft] = useState<Draft>(() => readDraft(key, isDraft) ?? (list ? fromList(list) : emptyDraft(me?.id ?? '')))
  const [errors, setErrors] = useState<{ name?: string; period?: string; general?: string }>({})
  const [busy, setBusy] = useState(false)
  const [filterOpen, setFilterOpen] = useState(false)
  const [register, setRegister] = useState<Register | null>(null)
  const options = useFilterOptions(register, draft.filter)
  // The owner of a new list is the person logged in, as soon as that is known.
  const current: Draft = { ...draft, owner: draft.owner || (list ? '' : (me?.id ?? '')) }
  const baseline = JSON.stringify(list ? fromList(list) : emptyDraft(me?.id ?? ''))
  useDirtyGuard(JSON.stringify(current) !== baseline)

  function change(patch: Partial<Draft>) {
    setDraft((current) => {
      const next = { ...current, ...patch }
      writeDraft(key, next)
      return next
    })
  }

  async function openFilter() {
    setFilterOpen(true)
    if (register) return
    try {
      setRegister(await getRegister('contacten'))
    } catch (caught) {
      setFilterOpen(false)
      toastFailure(caught, 'Het filter laden mislukt.')
    }
  }

  async function submit(event: FormEvent) {
    event.preventDefault()
    if (busy) return
    if (draft.name.trim() === '') {
      setErrors({ name: 'Geef de lijst een naam.' })
      return
    }
    setBusy(true)
    setErrors({})
    try {
      const input = toInput(current)
      const saved = list ? await updateListV2(list.id, input) : await createListV2(input)
      clearDraft(key)
      onOpenChange(false)
      toast.success(list ? 'Lijst bijgewerkt.' : `Lijst '${saved.name}' aangemaakt.`)
      onSaved(saved)
    } catch (caught) {
      const message = caught instanceof Error ? caught.message : 'Opslaan mislukt.'
      // The server's Dutch message goes where it belongs.
      if (caught instanceof CrmApiError && /einddatum|begindatum/i.test(message)) setErrors({ period: message })
      else if (caught instanceof CrmApiError && /naam/i.test(message)) setErrors({ name: message })
      else setErrors({ general: message })
    } finally {
      setBusy(false)
    }
  }

  const members = [...(membersById?.values() ?? [])]
  const count = conditionCount(draft.filter)
  return (
    <DialogContent size="lg">
      <DialogHeader>
        <DialogTitle>{list ? `Lijst '${list.name}' bewerken` : 'Nieuwe lijst'}</DialogTitle>
        <DialogDescription>Een groep mensen voor een event, een mailing of een eigen selectie.</DialogDescription>
      </DialogHeader>
      <form onSubmit={(event) => void submit(event)} className="mt-4 flex flex-col gap-4">
        <FormField label="Naam" required error={errors.name}>
          <Input maxLength={200} value={draft.name} onChange={(event) => change({ name: event.target.value })} autoFocus />
        </FormField>
        <div className="flex flex-col gap-1.5">
          <CategorySelect label="Soort" scope="lijst" value={draft.kind} onChange={(kind) => change({ kind })} />
        </div>
        <div className="grid gap-3 md:grid-cols-2">
          <FormField label="Datum">
            <Input type="date" value={draft.starts_on} onChange={(event) => change({ starts_on: event.target.value })} />
          </FormField>
          <FormField label="Einddatum (optioneel)" error={errors.period} hint="Een periode loopt van de datum t/m de einddatum.">
            <Input type="date" value={draft.ends_on} onChange={(event) => change({ ends_on: event.target.value })} />
          </FormField>
        </div>
        <div className="grid gap-3 md:grid-cols-2">
          <FormField label="Plaats">
            <Input maxLength={200} value={draft.location} onChange={(event) => change({ location: event.target.value })} />
          </FormField>
          <FormField label="Eigenaar">
            <NativeSelect value={current.owner} onChange={(event) => change({ owner: event.target.value })}>
              {current.owner === '' ? <option value="">—</option> : null}
              {members.map((member) => (
                <option key={member.id} value={member.id}>
                  {member.display_name}
                </option>
              ))}
            </NativeSelect>
          </FormField>
        </div>
        <div className="flex flex-col gap-1.5">
          <span className="text-sm font-semibold">Omschrijving</span>
          <RichTextEditor format="html" value={draft.description_html} onChange={(html) => change({ description_html: html })} ariaLabel="Omschrijving" />
        </div>
        <fieldset className="flex flex-col gap-2">
          <legend className="text-sm font-semibold">Type</legend>
          <label className="flex min-h-8 cursor-pointer items-center gap-2 text-sm max-md:min-h-11">
            <input
              type="radio"
              name={group}
              className="size-4 shrink-0 accent-primary"
              checked={draft.type === 'static'}
              disabled={list !== undefined}
              onChange={() => change({ type: 'static' })}
            />
            Statisch – je kiest zelf wie erop staat
          </label>
          <label className="flex min-h-8 cursor-pointer items-center gap-2 text-sm max-md:min-h-11">
            <input
              type="radio"
              name={group}
              className="size-4 shrink-0 accent-primary"
              checked={draft.type === 'dynamic'}
              disabled={list !== undefined}
              onChange={() => change({ type: 'dynamic' })}
            />
            Dynamisch – de leden volgen een filter op Contacten
          </label>
          {draft.type === 'dynamic' ? (
            <div className="flex flex-wrap items-center gap-3 pl-6">
              <Button type="button" variant="outline" size="sm" onClick={() => void openFilter()}>
                Filter instellen…
              </Button>
              <span className="text-sm text-muted-foreground">{count === 0 ? 'Nog geen filter: alle contacten.' : `${count} ${count === 1 ? 'voorwaarde' : 'voorwaarden'} ingesteld.`}</span>
            </div>
          ) : null}
        </fieldset>
        {errors.general ? (
          <p role="alert" className="text-sm text-destructive">
            {errors.general}
          </p>
        ) : null}
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              clearDraft(key)
              onOpenChange(false)
            }}
          >
            Annuleren
          </Button>
          <Button type="submit" disabled={busy}>
            {busy ? 'Bezig…' : list ? 'Opslaan' : 'Lijst aanmaken'}
          </Button>
        </DialogFooter>
      </form>
      {register ? (
        <AdvancedFilterSheet
          open={filterOpen}
          onOpenChange={setFilterOpen}
          register={register}
          filter={draft.filter}
          options={options}
          today={todayIso()}
          noun={contactNoun}
          count={(candidate) => countRecords('contacten', candidate)}
          onApply={(next) => {
            change({ filter: next })
            setFilterOpen(false)
          }}
        />
      ) : null}
    </DialogContent>
  )
}
